import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2, Phone, PhoneCall, PhoneMissed, Clock,
  CheckCircle2, Circle, Mic, Video,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { hasActiveCreatorProfile } from '../../lib/creatorPresentation';
import { getMyCallSettings, updateMyCallSettings, getCallsHistory } from '../../api/o2oCalls';

const STATE_OPTIONS = [
  { value: 'available', dot: 'bg-emerald-400', active: 'border-emerald-500 bg-emerald-700 text-white' },
  { value: 'offline', dot: 'bg-neutral-300', active: 'border-neutral-500 bg-neutral-700 text-white' },
];

const END_LABEL = {
  COMPLETED: 'Completata',
  REJECTED: 'Rifiutata',
  TIMEOUT: 'Senza risposta',
  CANCELLED: 'Annullata',
};

const REQ_LABELS = {
  vip: 'Diventa Creator',
  rate: 'Seleziona la tariffa al minuto',
  topics: 'Scegli gli argomenti trattati',
  mode: 'Scegli la modalità della chiamata',
  bio: 'Completa la bio (minimo 50 caratteri)',
};

export const CreatorCallHistory = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getCallsHistory('creator', 30).then(setItems).catch(() => setItems([]));
  }, []);
  if (items === null) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="o2o-call-history-empty">
        Nessuna chiamata ricevuta finora.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border rounded-xl border border-border bg-card" data-testid="o2o-call-history-list">
      {items.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            {c.state === 'COMPLETED'
              ? <PhoneCall className="h-4 w-4 text-emerald-500 shrink-0" />
              : <PhoneMissed className="h-4 w-4 text-red-400 shrink-0" />}
            <span className="font-semibold truncate">@{c.caller?.username || '—'}</span>
            <span className="text-[11px] text-muted-foreground">
              {new Date(c.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">{END_LABEL[c.state] || c.state}</span>
            {c.state === 'COMPLETED' && (
              <>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" /> {c.billed_minutes} min
                </span>
                <span className="font-semibold gold-text">+{c.creator_net_credits} cr</span>
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

const SectionTitle = ({ children }) => (
  <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground first:mt-0">{children}</p>
);

const CreatorCallSettings = () => {
  const { t } = useTranslation('o2o');
  const { toast } = useToast();
  const { user, refresh: refreshMe } = useAuth();
  const alreadyActive = hasActiveCreatorProfile(user);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const savingRef = useRef(false);
  const revisionRef = useRef(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (document.hidden || savingRef.current) return;
      const revision = ++revisionRef.current;
      try {
        const result = await getMyCallSettings();
        if (active && revision === revisionRef.current) setData(result);
      } catch (_) { /* Existing values remain editable on a transient error. */ }
    };
    load();
    const timer = setInterval(load, 10000);
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', load);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', load); document.removeEventListener('visibilitychange', load); };
  }, []);

  const save = async (patch, successTitle = 'Impostazioni chiamate aggiornate') => {
    if (busy) return;
    setBusy(true);
    savingRef.current = true;
    revisionRef.current += 1;
    try {
      const fresh = await updateMyCallSettings(patch);
      setData(fresh);
      window.dispatchEvent(new Event('nymia:o2o-state-changed'));
      if ('rate_credits_per_min' in patch) refreshMe?.();
      toast({ title: successTitle });
    } catch (e) {
      toast({
        title: 'Operazione non riuscita',
        description: String(e.response?.data?.detail || '').split('|').pop() || '',
        variant: 'destructive',
      });
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  };

  const toggleTopic = (topic) => {
    const current = data.topics || [];
    const next = current.includes(topic)
      ? current.filter((t) => t !== topic)
      : [...current, topic];
    if (next.length > 5) {
      toast({ title: 'Puoi scegliere al massimo 5 argomenti', variant: 'destructive' });
      return;
    }
    save({ topics: next }, 'Argomenti aggiornati');
  };

  if (!data) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const missing = data.missing_requirements || [];
  const ready = missing.length === 0;

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="creator-call-settings">
      <div className="mb-4 flex items-center gap-2">
        <Phone className="h-4 w-4 gold-text" />
        <h3 className="text-sm font-semibold">Chiamate · pagamento al minuto</h3>
      </div>

      {!data.is_vip && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground" data-testid="o2o-vip-required-note">
          {alreadyActive ? t('creator_presentation.unavailable_description', { ns: 'common' }) : <>
            Il servizio Chiamate richiede <span className="font-semibold gold-text">Voce Pro</span> attiva.
            Completa l'attivazione Voce Pro per attivarlo.
          </>}
        </div>
      )}

      {/* Requirements checklist */}
      {!ready && (
        <div className="mb-4 rounded-lg border border-border bg-secondary/30 p-3" data-testid="o2o-requirements-checklist">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Per metterti Disponibile completa:
          </p>
          <ul className="space-y-1">
            {Object.keys(REQ_LABELS).filter((k) => k !== 'vip' || !alreadyActive || !data.is_vip).map((k) => (
              <li key={k} className="flex items-center gap-2 text-xs" data-testid={`o2o-requirement-${k}`}>
                {missing.includes(k)
                  ? <Circle className="h-3.5 w-3.5 text-red-400" />
                  : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                <span className={missing.includes(k) ? 'text-foreground' : 'text-muted-foreground line-through'}>
                  {k === 'vip' && alreadyActive ? t('creator_presentation.unavailable_title', { ns: 'common' }) : REQ_LABELS[k]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SectionTitle>Il tuo stato</SectionTitle>
      <div className="mb-1 flex flex-wrap gap-2">
        {STATE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => save({ state: opt.value })}
            disabled={busy}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
              (data.state === 'available' ? 'available' : 'offline') === opt.value ? opt.active : 'border-border text-muted-foreground hover:bg-muted'
            }`}
            aria-pressed={(data.state === 'available' ? 'available' : 'offline') === opt.value}
            data-testid={`o2o-state-${opt.value}`}
          >
            <span className={`h-2.5 w-2.5 rounded-full ${opt.dot}`} />
            {t(`call_availability.${opt.value}`)}
          </button>
        ))}
      </div>
      {data.in_call && (
        <p className="mb-2 text-[11px] text-red-500" data-testid="o2o-in-call-note">
          {t('call_availability.in_call_note')}
        </p>
      )}
      {data.state === 'available' && (
        <p className="mb-2 text-[11px] text-emerald-600">
          Un post automatico è visibile nel feed finché resti Disponibile.
        </p>
      )}

      <SectionTitle>Tariffa al minuto *</SectionTitle>
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={data.rate_credits_per_min || ''}
          onChange={(e) => save({ rate_credits_per_min: Number(e.target.value) })}
          disabled={busy}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
          data-testid="o2o-rate-select"
        >
          <option value="" disabled data-testid="o2o-rate-option-placeholder">{'Seleziona tariffa…'}</option>
          {(data.rate_options || []).map((v) => (
            <option key={v} value={v} data-testid={`o2o-rate-option-${v}`}>{`${v} crediti/min`}</option>
          ))}
        </select>
        {data.rate_credits_per_min > 0 && (
          <span className="text-xs text-muted-foreground">
            Max {data.config?.max_duration_minutes} min a chiamata ·
            fino a {data.rate_credits_per_min * (data.config?.max_duration_minutes || 0)} crediti
          </span>
        )}
      </div>

      <SectionTitle>Modalità della chiamata *</SectionTitle>
      <div className="flex gap-2">
        {[
          { v: 'audio', label: 'Solo Audio', Icon: Mic },
          // Phase F3 — Voice-First: video mode hidden when the admin flag is OFF.
          ...(data.config?.video_enabled ? [{ v: 'audio_video', label: 'Audio + Video', Icon: Video }] : []),
        ].map(({ v, label, Icon }) => (
          <button
            key={v}
            onClick={() => save({ o2o_mode: v }, 'Modalità aggiornata')}
            disabled={busy}
            className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
              data.o2o_mode === v
                ? 'border-amber-500 bg-amber-500/10 gold-text'
                : 'border-border text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`o2o-mode-${v}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      <SectionTitle>Argomenti trattati * <span className="normal-case font-normal">(max 5)</span></SectionTitle>
      <div className="flex flex-wrap gap-2" data-testid="o2o-topics-picker">
        {(data.topic_options || []).map((topic) => {
          const active = (data.topics || []).includes(topic);
          return (
            <button
              key={topic}
              onClick={() => toggleTopic(topic)}
              disabled={busy}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${
                active
                  ? 'border-amber-500 bg-amber-500/15 gold-text'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
              data-testid={`o2o-topic-${topic}`}
            >
              {topic}
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">
        Quando sei <span className="font-semibold text-emerald-600">Disponibile</span> gli utenti possono chiamarti subito.
        I crediti vengono addebitati solo per i minuti effettivi di conversazione.
      </p>
    </section>
  );
};

export default CreatorCallSettings;
